import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, Grid2, Paper } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useState } from "react";
import { PartLog } from "../../models/Part";
import { getPartLogs } from "../../repositories/PartsReportRepository";
import { useTableData } from "./useTableData";

const usePartLogs = (partId: number, onError?: (error: Error) => void) => {
	return useQuery<PartLog[], Error>({
		queryKey: ["partLogs", partId],
		queryFn: ({ signal }) => getPartLogs({ partId }, signal),
		onError: onError,
	});
};

export const LogsModal = ({
	partId,
	partName,
}: {
	partId: number;
	partName: String;
}) => {
	const [filterValue, setFilterValue] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [isOpen, setOpen] = useState(false);
	const [error, setError] = useState("");

	const { data: partLogs, isLoading } = usePartLogs(partId, (error) =>
		setError(error.message),
	);

	const { columnDefs } = useTableData();
	const { defaultColDef } = useGridDefinitions({});

	return (
		<>
			<Button
				fullWidth
				variant="contained"
				color="primary"
				onClick={() => setOpen(true)}
				data-test-id="show-logs-button"
			>
				Show logs
			</Button>
			<MesfModal
				open={isOpen}
				handleClose={() => setOpen(false)}
				maxWidth="xl"
				title={`Logs History of: ${partName}`}
			>
				<MesfModal.Content dividers style={{ padding: "15px", width: "150vh" }}>
					<Grid2 container spacing={2} style={{ padding: "0 15px" }}>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<TextFieldSearchFilter
								value={filterValue}
								onChange={(val) => {
									setFilterValue(val);
									gridApi?.setGridOption("quickFilterText", val);
								}}
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<Paper elevation={4} style={{ padding: 6 }}>
								<div style={{ height: "40vh", width: "100%", padding: 5 }}>
									<AgGridReact<PartLog>
										loading={isLoading}
										rowData={partLogs?.filter((log) => log.LogId)}
										columnDefs={columnDefs}
										defaultColDef={defaultColDef}
										rowHeight={40}
										headerHeight={34}
										animateRows
										suppressContextMenu
										onGridReady={(params) => setGridApi(params.api)}
										loadingOverlayComponent={CenteredLazyLoading}
										getRowId={(params) => `${params.data.LogId}`}
										gridOptions={{
											rowSelection: {
												checkboxes: false,
												enableClickSelection: true,
												mode: "singleRow",
											},
											localeText: {
												noRowsToShow: "No logs to show",
											},
										}}
									/>
								</div>
							</Paper>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "15px 30px" }}>
					<Button
						fullWidth
						variant="contained"
						color="primary"
						onClick={() => setOpen(false)}
					>
						CLOSE
					</Button>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
